/**
 * lib/feed.ts — the client-side discovery blend (P11).
 *
 * The home feed is assembled HERE, in the client, over three subscribed sources:
 * the server-materialized algorithmic `feedRank` (40%), the per-viewer Social feed
 * derived from validated Orbits + the vouch graph (40%), and upcoming `event`s
 * (20%). The Patronage Board is a separate always-on list. Shared by
 * `DiscoveryModule` and the headless harness so the 40/40/20 + the forbidden-
 * mechanic ban are tested as a pure function.
 *
 * THE BAN (algorithmic-only discovery is forbidden): the algorithmic bucket is
 * sliced to its 40% cap and NEVER refilled from another surface's leftover slots.
 * An empty social/event surface leaves its slots empty (an empty state) — the
 * feed can never collapse to 100% algorithm. That is enforced by the ABSENCE of
 * any "redistribute leftovers to algo" code path, not by a runtime guard.
 */

import type { DbConnection } from '../module_bindings/index.js';
import type { Identity } from 'spacetimedb';

/** The default blend weights (basis points; mirror the server FEED_* constants). */
export const FEED_ALGO_BPS = 4_000;
export const FEED_SOCIAL_BPS = 4_000;
export const FEED_EVENT_BPS = 2_000;
const BPS_DENOM = 10_000;

/** A discovery surface tag (the four surfaces a player browses). */
export type FeedSurface = 'algo' | 'social' | 'events' | 'patronage';

/** The three weighted sources feeding the 40/40/20 blend (world ids; events are event ids). */
export interface FeedSources {
  /** Algorithmic "What's Hot" — world ids from `feedRank`, hottest first. */
  algo: bigint[];
  /** Social "Your Orbit" — world ids from validated Orbits + vouched creators. */
  social: bigint[];
  /** Event Calendar "What's On" — upcoming event ids. */
  events: bigint[];
}

/** The assembled feed: each surface filled to its weighted slot count (never beyond). */
export interface BlendedFeed {
  algo: bigint[];
  social: bigint[];
  events: bigint[];
  algoSlots: number;
  socialSlots: number;
  eventSlots: number;
}

/**
 * Assemble the home feed over `totalSlots` at the 40/40/20 blend. Each surface is
 * filled up to its weighted slot count from its own source ONLY — a short or empty
 * surface simply yields fewer items (its remaining slots are empty states). The
 * algorithmic bucket is capped at `algoSlots` and never grows to fill another
 * surface's gap: that is the structural enforcement of the algorithm-only ban.
 */
export function assembleHomeFeed(sources: FeedSources, totalSlots: number): BlendedFeed {
  const slots = totalSlots > 0 ? Math.floor(totalSlots) : 0;
  const algoSlots = Math.floor((slots * FEED_ALGO_BPS) / BPS_DENOM);
  const socialSlots = Math.floor((slots * FEED_SOCIAL_BPS) / BPS_DENOM);
  const eventSlots = slots - algoSlots - socialSlots; // the remainder (~20%)
  return {
    algo: sources.algo.slice(0, algoSlots),
    social: sources.social.slice(0, socialSlots),
    events: sources.events.slice(0, eventSlots),
    algoSlots,
    socialSlots,
    eventSlots,
  };
}

/** The algorithmic "What's Hot" world ids, in materialized rank order (from `feedRank`). */
export function algoWorlds(conn: DbConnection): bigint[] {
  return [...conn.db.feedRank.iter()]
    .sort((a, b) => a.rankPos - b.rankPos)
    .map((r) => r.worldId);
}

/**
 * The per-viewer Social feed world ids, derived client-side: the modal world of
 * each VALIDATED Orbit the viewer belongs to (unvalidated/bot Orbits are ignored —
 * the anti-bot guarantee), plus worlds owned by creators the viewer actively
 * vouches for. Reads only small, already-subscribed P6/P7 tables.
 */
export function deriveSocialWorlds(conn: DbConnection, viewer: Identity): bigint[] {
  const worlds = new Set<string>();

  // (1) Validated Orbits the viewer is a member of → their preferred (modal) world.
  const myOrbitIds = new Set<string>();
  for (const m of conn.db.orbitMember.iter()) {
    if (m.identity.isEqual(viewer)) myOrbitIds.add(m.orbitId.toString());
  }
  for (const o of conn.db.orbit.iter()) {
    if (myOrbitIds.has(o.orbitId.toString()) && o.validated && o.preferredWorldId !== 0n) {
      worlds.add(o.preferredWorldId.toString());
    }
  }

  // (2) Worlds owned by creators the viewer actively vouches for.
  const vouchees = new Set<string>();
  for (const v of conn.db.vouch.iter()) {
    if (v.active && v.voucher.isEqual(viewer)) vouchees.add(v.vouchee.toHexString());
  }
  if (vouchees.size > 0) {
    for (const w of conn.db.world.iter()) {
      if (vouchees.has(w.owner.toHexString())) worlds.add(w.worldId.toString());
    }
  }

  return [...worlds].map((s) => BigInt(s));
}

/** Upcoming events for the Event Calendar surface (start time ≥ now), soonest first. */
export function upcomingEvents(conn: DbConnection, nowMicros: bigint): bigint[] {
  return [...conn.db.event.iter()]
    .filter((e) => e.startAt.microsSinceUnixEpoch >= nowMicros)
    .sort((a, b) =>
      a.startAt.microsSinceUnixEpoch < b.startAt.microsSinceUnixEpoch
        ? -1
        : a.startAt.microsSinceUnixEpoch > b.startAt.microsSinceUnixEpoch
          ? 1
          : 0,
    )
    .map((e) => e.eventId);
}

/**
 * The Patronage Board: the creators the viewer actively supports via a P10
 * `patronage` subscription. Always shown alongside the blend, BYPASSING the
 * algorithm entirely (a supported creator appears regardless of their ranking).
 */
export function patronageCreators(conn: DbConnection, viewer: Identity): string[] {
  const out: string[] = [];
  for (const s of conn.db.subscription.iter()) {
    if (s.kind === 'patronage' && s.active && s.patron.isEqual(viewer)) out.push(s.creator.toHexString());
  }
  return out;
}
